import { WEI, } from "@bracket/blocksmith";
import { raise, } from "@bracket/catch";
import { getDateTime, getEpochEndTime, getEpochStartTime, getExpectedEpochForDay, } from "@bracket/parlance";
import type { IVaultHasDenomination, IVaultHasLoadedNavs, IVaultHasNavCollectionStatus, IVaultHasNavs, Vault, } from "@bracket/types";
import { DateTime, } from "luxon";


export declare type GetNavDataOptions = {
	scope?: { days?: number; epoch?: Vault["epoch"]; };
}

export declare type GetNavDataVaultTraits = IVaultHasNavs & IVaultHasDenomination;


export const getNavData = async <V extends Vault = Vault>( vault: V & GetNavDataVaultTraits, options: GetNavDataOptions = {} ): Promise<V & GetNavDataVaultTraits & IVaultHasLoadedNavs & IVaultHasNavCollectionStatus> => {

	try {

		const { scope = { days: 60, }, } = options;
		const { launch_date, navs: vaultNavs, denomination, epoch_duration_days, epoch, nav_launch, } = vault;
		const daysSinceLaunch = DateTime.fromISO(launch_date).diffNow("days").days * -1;
		const expectedEpoch = getExpectedEpochForDay(vault, daysSinceLaunch);
		const timeSortedNavs = vaultNavs.filter(nav => nav.epoch !== 0).sort((a, b) => a.epoch - b.epoch);
		const navsWithEpochOne = (() => {
			if(timeSortedNavs.some(nav => nav.epoch === 1)) {
				return timeSortedNavs;
			}

			const seedNav = vaultNavs.find(nav => nav.epoch === 0) || vaultNavs[0];
			if(!seedNav) {
				return timeSortedNavs;
			}

			const launchNav = typeof nav_launch === "string" && nav_launch.length ? nav_launch : seedNav.nav;
			return [ ...timeSortedNavs, { ...seedNav, epoch: 1, nav: launchNav, onav: launchNav, epoch_duration_days, }, ].sort((a, b) => a.epoch - b.epoch);
		})();
		const requestedScopedDays = scope.days || (scope.epoch ? scope.epoch * epoch_duration_days : raise("vaults::getNavData - no scope provided"));
		const scopedDays = requestedScopedDays > daysSinceLaunch ? daysSinceLaunch : requestedScopedDays;
		const scopedEpochs = Math.floor(scopedDays / epoch_duration_days);
		const scopedNavs = navsWithEpochOne.slice(scopedEpochs * -1); // latest nav we have minus the number of epochs in scope
		
		const loadedNavs = navsWithEpochOne.map(nav => {
			try {
				const daysDifference = DateTime.fromISO(nav.updated.toString()).diff(DateTime.fromISO(launch_date), "days").days;
				if(daysDifference < 0) {
					// console.warn(`getNavData failed for vault ${vault.symbol} - daysDifference < 0 for nav ${nav.epoch} - ${daysDifference}`);
					return { ...nav, annualized: 0, }
				}

				const annualized = WEI.fromSync(nav.nav, denomination).pow(365 / daysDifference).subtract(1).multiply(100).toNumber();
				// const annualizedNum = ((WEI.fromSync(nav.nav, denomination).toNumber() ** (365 / daysDifference)) - 1) * 100;
				// console.log("CHECK>>>", annualized === annualizedNum, annualized, annualizedNum);

				return { ...nav, annualized, }
			}
			catch(err) {
				console.error(`getNavData failed for vault ${vault.symbol} - ${err instanceof Error ? err.message : "without error instance"}`);
				return { ...nav, annualized: 0, }
			}
		});
		return {
			...vault,
			navs:	loadedNavs,
			navCollectionStatus: {
				launch: getDateTime(launch_date),
				start: getEpochStartTime({ vault, epoch: scopedNavs[0].epoch, }),
				end: getEpochEndTime({ vault, epoch: scopedNavs[scopedNavs.length -1].epoch, }),
				days: scopedDays,
				epochDays: epoch_duration_days,
				current: epoch === expectedEpoch,
			},
		};
	
	}
	catch(err) {

		throw new Error(`getNavData failed for vault ${vault.symbol} - ${err instanceof Error ? err.message : "without error instance"}`);
	}

}
